import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Container, Overline, Reveal } from "@/components/primitives";
import { useFoundingMembers, FoundingCard } from "@/components/sections/FoundingMembers";

export default function Collection() {
  const [props, setProps] = useState(null);
  const founding = useFoundingMembers();

  useEffect(() => {
    api.get("/collection").then((r) => setProps(r.data.properties)).catch(() => setProps([]));
  }, []);

  const foundingMembers = founding?.properties || [];

  return (
    <div className="relative z-10">
      <section className="pt-40 md:pt-52 pb-16 text-center px-6" data-testid="collection-page">
        <Reveal>
          <Overline>The VillaFlow Private Collection</Overline>
          <h1 className="mt-8 font-serif font-light text-4xl md:text-7xl text-ivory tracking-tight max-w-4xl mx-auto leading-[1.05]">
            A curated network of exceptional villas.
          </h1>
        </Reveal>
      </section>

      {foundingMembers.length > 0 && (
        <section className="pb-24 border-y border-ivory/10 bg-navy-800 py-20" data-testid="founding-band">
          <Container>
            <Reveal>
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
                <div>
                  <Overline>The Founding Collection</Overline>
                  <h2 className="mt-4 font-serif font-light text-3xl md:text-5xl text-ivory tracking-tight">Founding Members.</h2>
                </div>
                <Link to="/founding-members" className="group inline-flex items-center gap-3 text-ivory hover:text-gold transition-colors duration-500 text-xs tracking-[0.2em] uppercase" data-testid="view-all-founding">
                  View the Founding Collection
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-500" />
                </Link>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {foundingMembers.slice(0, 3).map((p, i) => <FoundingCard key={p.id} p={p} index={i} />)}
            </div>
          </Container>
        </section>
      )}

      <Container className="pb-40 pt-24">
        {props === null ? (
          <p className="text-center text-ivory/40 py-20 text-sm tracking-widest uppercase">Loading the Collection…</p>
        ) : props.length === 0 ? (
          <Reveal>
            <div className="text-center py-24 border-y border-ivory/10 max-w-2xl mx-auto">
              <p className="font-serif italic text-3xl text-ivory/70">The Collection is being curated.</p>
              <p className="mt-6 text-ivory/50 font-light">
                Selected properties will appear here as the Founding Collection is assembled.
              </p>
              <Link to="/apply" className="mt-10 inline-block border border-gold/50 hover:border-gold text-ivory hover:text-gold px-10 py-4 text-xs tracking-[0.2em] uppercase transition-colors duration-500" data-testid="collection-apply-cta">
                Request Consideration
              </Link>
            </div>
          </Reveal>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {props.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1}>
                <Link to={`/collection/${p.slug}`} className="group block" data-testid={`property-card-${p.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={p.hero_image ? fileUrl(p.hero_image) : IMAGES.amalfiVilla2} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                    <div className="absolute inset-0 bg-navy-900/20 group-hover:bg-navy-900/10 transition-colors duration-700" />
                    {p.founding_member && (
                      <span className="absolute top-4 left-4 bg-navy-900/80 backdrop-blur text-gold text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border border-gold/30">Founding Member</span>
                    )}
                  </div>
                  <div className="mt-6">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-gold">VillaFlow Selected Property</span>
                    <h3 className="font-serif text-2xl md:text-3xl text-ivory mt-2 group-hover:text-gold transition-colors duration-500">{p.name}</h3>
                    <p className="text-ivory/50 text-sm mt-1 font-light">{p.location}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
}
